import fallbackPrograms from '../../data/programs.json';
import { DEGREE_SAMPLES } from '../data/degreeSamples';
import type { PortalDataResult, Program, SheetSummary } from '../types';
import { clean, slug } from '../utils/text';

const SHEET_ID = '2PACX-1vScO3qB_HPqzLRl-V4HQ7FP5MBEqFbuJwY8L7dIp95ruA1ICJ6-7RujlpdfSRj-Sw';
const WORKBOOK_URL = `https://docs.google.com/spreadsheets/d/e/${SHEET_ID}/pubhtml`;

function csvUrl(gid: string) {
  return `https://docs.google.com/spreadsheets/d/e/${SHEET_ID}/pub?gid=${gid}&single=true&output=csv`;
}

function parseCsv(text: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];
    if (char === '"' && quoted && next === '"') {
      value += '"';
      index += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === ',' && !quoted) {
      row.push(value.trim());
      value = '';
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && next === '\n') index += 1;
      row.push(value.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      value = '';
    } else {
      value += char;
    }
  }
  if (value || row.length) {
    row.push(value.trim());
    if (row.some(Boolean)) rows.push(row);
  }
  return rows;
}

function normalizedHeader(value: string) {
  return clean(value)
    .toLowerCase()
    .replace(/[₹()]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function feeNumber(value?: string) {
  const firstAmount = clean(value).match(/[\d][\d,]*/);
  return firstAmount ? Number(firstAmount[0].replace(/,/g, '')) : null;
}

function findHeaderIndex(rows: string[][]) {
  return rows.findIndex((row) => {
    const headers = row.map(normalizedHeader);
    const hasProgram = headers.some((header) => ['program', 'program name', 'programs'].includes(header));
    const hasUniversity = headers.some((header) => /university|platform/.test(header));
    const hasDetails = headers.some((header) => /fee|duration|specialization|eligibility/.test(header));
    return hasProgram && (hasUniversity || hasDetails);
  });
}

function headerIndex(headers: string[], aliases: string[]) {
  return headers.findIndex((header) => aliases.some((alias) => header === alias || header.includes(alias)));
}

function exactHeaderIndex(headers: string[], aliases: string[]) {
  return headers.findIndex((header) => aliases.includes(header));
}

function preferredHeaderIndex(headers: string[], aliases: string[]) {
  for (const alias of aliases) {
    const exact = headers.findIndex((header) => header === alias);
    if (exact >= 0) return exact;
  }
  return headerIndex(headers, aliases);
}

function universityFromSheet(sheetName: string) {
  return clean(sheetName)
    .replace(/\s*-\s*programs?.*$/i, '')
    .replace(/\s+programs?.*$/i, '');
}

function readField(row: string[], indexes: Record<string, number>, key: string) {
  return indexes[key] >= 0 ? clean(row[indexes[key]]) : '';
}

function normalizeRows(rows: string[][], sheetName = ''): Program[] {
  const headerRowIndex = findHeaderIndex(rows);
  if (headerRowIndex < 0) return [];

  const headers = rows[headerRowIndex].map(normalizedHeader);
  const indexes = {
    university: exactHeaderIndex(headers, ['university platform', 'university', 'platform']),
    program: headerIndex(headers, ['program name', 'program']),
    degree: headerIndex(headers, ['degree level', 'degree type', 'degree']),
    specializations: headerIndex(headers, ['specializations', 'specialization', 'domain']),
    duration: headerIndex(headers, ['program duration', 'duration']),
    fee: headerIndex(headers, ['total fee inr', 'total fees inr', 'website price inr', 'total fee', 'website price']),
    emi: headerIndex(headers, ['emi options', 'emi from inr mo', 'emi finance', 'emi']),
    eligibility: headerIndex(headers, ['eligibility criteria', 'eligibility']),
    accreditation: headerIndex(headers, ['accreditation', 'approvals']),
    link: preferredHeaderIndex(headers, ['program url', 'source url', 'url', 'official link']),
    brochure: preferredHeaderIndex(headers, [
      'brochure url',
      'brochure link',
      'brochure download link',
      'brochure pdf',
      'download brochure',
      'download link',
      'program brochure',
      'pdf link',
      'prospectus url',
      'prospectus link',
      'brochure',
    ]),
    degreeSample: preferredHeaderIndex(headers, ['degree sample', 'sample degree', 'certificate sample', 'sample certificate']),
    scholarship: headerIndex(headers, ['max waiver inr', 'scholarship', 'waiver', 'discount']),
    placement: headerIndex(headers, ['placements', 'placement', 'career support']),
  };
  const fallbackUniversity = universityFromSheet(sheetName);

  return rows
    .slice(headerRowIndex + 1)
    .map((row) => {
      const university = readField(row, indexes, 'university') || fallbackUniversity;
      const link = readField(row, indexes, 'link');
      const brochure = readField(row, indexes, 'brochure');
      const degreeSample = readField(row, indexes, 'degreeSample');
      return {
        id: '',
        university,
        program: readField(row, indexes, 'program'),
        degree: readField(row, indexes, 'degree'),
        specializations: readField(row, indexes, 'specializations'),
        duration: readField(row, indexes, 'duration'),
        fee: readField(row, indexes, 'fee'),
        feeValue: feeNumber(readField(row, indexes, 'fee')),
        emi: readField(row, indexes, 'emi'),
        eligibility: readField(row, indexes, 'eligibility'),
        accreditation: readField(row, indexes, 'accreditation'),
        scholarship: readField(row, indexes, 'scholarship'),
        placement: readField(row, indexes, 'placement'),
        link: /^https?:\/\//i.test(link) ? link : '',
        brochure: /^https?:\/\//i.test(brochure) ? brochure : '',
        degreeSample: /^https?:\/\//i.test(degreeSample) ? degreeSample : '',
        sourceSheet: sheetName,
      };
    })
    .filter(
      (item) =>
        item.university &&
        item.program &&
        !/^#(name|ref|n\/a)/i.test(item.program) &&
        !/^-+\s*(ug|pg|diploma|certificate)?\s*programs?\s*-+$/i.test(item.program),
    );
}

function discoverSheets(html: string) {
  const sheets: { name: string; gid: string }[] = [];
  const pattern = /items\.push\(\{name:\s*"([^"]+)"[\s\S]*?gid:\s*"(-?\d+)"/g;
  let match = pattern.exec(html);
  while (match) {
    sheets.push({ name: decodeSheetName(match[1]), gid: match[2] });
    match = pattern.exec(html);
  }
  return sheets;
}

function decodeSheetName(value: string) {
  return value
    .replace(/\\x3d/g, '=')
    .replace(/\\u([0-9a-f]{4})/gi, (_, code: string) => String.fromCharCode(parseInt(code, 16)))
    .replace(/\\"/g, '"')
    .trim();
}

async function fetchRows(sheet: { name: string; gid: string }) {
  const response = await fetch(csvUrl(sheet.gid), { cache: 'no-store' });
  if (!response.ok) throw new Error(`${sheet.name} returned ${response.status}`);
  return parseCsv(await response.text());
}

function mergePrograms(programGroups: Program[][]) {
  const merged = new Map<string, Program>();
  programGroups.flat().forEach((program) => {
    const key = `${slug(program.university)}|${slug(program.program)}`;
    if (!key || key === '|') return;
    if (!merged.has(key)) {
      merged.set(key, program);
      return;
    }
    const current = merged.get(key)!;
    const patch: Partial<Program> = {};
    (Object.keys(program) as (keyof Program)[]).forEach((field) => {
      if ((!current[field] || current[field] === 'Partner-dependent') && program[field]) {
        patch[field] = program[field] as never;
      }
    });
    Object.assign(current, patch);
    current.sourceSheet = [...new Set([current.sourceSheet, program.sourceSheet].filter(Boolean))].join(', ');
  });

  return [...merged.values()]
    .sort((a, b) => a.university.localeCompare(b.university) || a.program.localeCompare(b.program))
    .map((program, index) => ({ ...program, id: `${slug(program.university)}-${index + 1}` }));
}

function normalizeMasterSummary(rows: string[][]) {
  const headerRowIndex = rows.findIndex(
    (row) => row.some((cell) => /university|platform/i.test(clean(cell))) && row.some((cell) => /accreditation/i.test(clean(cell))),
  );
  if (headerRowIndex < 0) return {};
  const headers = rows[headerRowIndex].map(normalizedHeader);
  const universityIndex = headerIndex(headers, ['university platform', 'university']);
  const linkIndex = headerIndex(headers, ['official source']);
  const accreditationIndex = headerIndex(headers, ['accreditation']);
  const coursesIndex = headerIndex(headers, ['courses available']);
  const mbaIndex = headerIndex(headers, ['mba specializations available']);
  const brochureIndex = preferredHeaderIndex(headers, [
    'brochure url',
    'brochure link',
    'brochure download link',
    'brochure pdf',
    'download brochure',
    'download link',
    'pdf link',
    'prospectus url',
    'brochure',
  ]);

  return rows.slice(headerRowIndex + 1).reduce<Record<string, SheetSummary>>((summary, row) => {
    const university = clean(row[universityIndex]);
    if (!university) return summary;
    summary[university] = {
      university,
      link: clean(row[linkIndex]),
      accreditation: clean(row[accreditationIndex]),
      courses: clean(row[coursesIndex]),
      mbaSpecializations: clean(row[mbaIndex]),
      brochure: brochureIndex >= 0 && /^https?:\/\//i.test(clean(row[brochureIndex])) ? clean(row[brochureIndex]) : '',
    };
    return summary;
  }, {});
}

function matchingSummary(university: string, summaries: Record<string, SheetSummary>) {
  if (summaries[university]) return summaries[university];
  const right = slug(university);
  return Object.values(summaries).find((item) => {
    const left = slug(item.university);
    return left && right && (left.includes(right) || right.includes(left));
  });
}

function canonicalizeUniversities(programGroups: Program[][], summaries: Record<string, SheetSummary>) {
  programGroups.flat().forEach((program) => {
    const summary = matchingSummary(program.university, summaries);
    if (summary) program.university = summary.university;
  });
  return programGroups;
}

function enrichFromSummary(programs: Program[], summaries: Record<string, SheetSummary>) {
  programs.forEach((program) => {
    const summary = matchingSummary(program.university, summaries);
    if (!summary) return;
    if (!program.accreditation) program.accreditation = summary.accreditation;
    if (!program.link && /^https?:\/\//i.test(summary.link || '')) program.link = summary.link;
    if (!program.brochure && summary.brochure) program.brochure = summary.brochure;
  });
  return programs;
}

function withDegreeSamples(programs: Program[]) {
  return programs.map((program) => {
    if (program.degreeSample) return program;
    const bySlug = DEGREE_SAMPLES[slug(program.university)];
    const partial = Object.entries(DEGREE_SAMPLES).find(([key]) => slug(program.university).includes(key) || key.includes(slug(program.university)));
    return { ...program, degreeSample: bySlug || partial?.[1] || '' };
  });
}

function normalizeFallback(programs: Program[]) {
  return withDegreeSamples(
    programs.map((item) => ({
      scholarship: '',
      placement: '',
      brochure: '',
      sourceSheet: 'Offline snapshot',
      ...item,
      feeValue: item.feeValue || feeNumber(item.fee),
    })),
  );
}

async function loadLiveWorkbook(): Promise<PortalDataResult> {
  const workbookResponse = await fetch(WORKBOOK_URL, { cache: 'no-store' });
  if (!workbookResponse.ok) throw new Error(`Workbook returned ${workbookResponse.status}`);
  const sheets = discoverSheets(await workbookResponse.text());
  const allProgramsSheet = sheets.find((sheet) => /^all programs$/i.test(sheet.name));
  const universitySheets = sheets.filter((sheet) => /programs?/i.test(sheet.name) && !/all programs|compare/i.test(sheet.name));
  const masterSheet = sheets.find((sheet) => /master summary/i.test(sheet.name));
  if (!allProgramsSheet && !universitySheets.length) throw new Error('No program sheets found');

  const programSheets = [...(allProgramsSheet ? [allProgramsSheet] : []), ...universitySheets];
  const results = await Promise.allSettled(programSheets.map(async (sheet) => normalizeRows(await fetchRows(sheet), sheet.name)));
  const programGroups = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
  if (!programGroups.flat().length) throw new Error('No program rows found');

  let summaries: Record<string, SheetSummary> = {};
  if (masterSheet) {
    try {
      summaries = normalizeMasterSummary(await fetchRows(masterSheet));
    } catch {
      summaries = {};
    }
  }

  return {
    programs: withDegreeSamples(enrichFromSummary(mergePrograms(canonicalizeUniversities(programGroups, summaries)), summaries)),
    summaries,
    sheets,
    source: 'live',
  };
}

export async function loadPortalData(): Promise<PortalDataResult> {
  try {
    return await loadLiveWorkbook();
  } catch (error) {
    return {
      programs: normalizeFallback(fallbackPrograms as Program[]),
      summaries: {},
      sheets: [{ name: 'Offline snapshot', gid: '' }],
      source: 'fallback',
      error,
    };
  }
}

export { WORKBOOK_URL };
